import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Sliders, Edit, Trash2, Eye, EyeOff, MessageSquare, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

const HEADLINE_TAGS = ["BREAKING", "TRENDING", "RECAP", "UPDATE", "ALERT"];
const TAG_COLORS = {
  BREAKING: "bg-red-600",
  TRENDING: "bg-orange-500",
  RECAP: "bg-blue-600",
  UPDATE: "bg-emerald-600",
  ALERT: "bg-yellow-500 text-black",
};

const HeadlineItem = ({ headline, onEdit, onDelete, onToggle, onMove, isFirst, isLast }) => (
  <div className={`flex items-center justify-between p-3 border ${headline.is_active ? 'bg-zinc-900/50 border-white/10' : 'bg-zinc-900/20 border-white/5 opacity-60'}`}>
    <div className="flex items-center gap-3 flex-1 min-w-0">
      <GripVertical className="w-4 h-4 text-zinc-600 flex-shrink-0" />
      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase flex-shrink-0 ${TAG_COLORS[headline.tag] || 'bg-zinc-700'} text-white`}>
        {headline.tag}
      </span>
      <span className="text-sm text-zinc-300 truncate">{headline.text}</span>
    </div>
    <div className="flex items-center gap-1 flex-shrink-0">
      <button onClick={() => onMove(headline.id, -1)} disabled={isFirst} className="p-1.5 text-zinc-600 hover:text-white disabled:opacity-30" title="Move up">
        <ArrowUp className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => onMove(headline.id, 1)} disabled={isLast} className="p-1.5 text-zinc-600 hover:text-white disabled:opacity-30" title="Move down">
        <ArrowDown className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => onToggle(headline.id)} className={`p-1.5 transition-colors ${headline.is_active ? 'text-green-500 hover:text-green-400' : 'text-zinc-500 hover:text-white'}`} title={headline.is_active ? "Disable" : "Enable"}>
        {headline.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
      <button onClick={() => onEdit(headline)} className="p-1.5 text-zinc-500 hover:text-white transition-colors" title="Edit">
        <Edit className="w-4 h-4" />
      </button>
      <button onClick={() => onDelete(headline.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" title="Delete">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </div>
);

export const TickerTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [newLink, setNewLink] = useState({ name: "", url: "", type: "espn", is_active: true });
  // Headlines state
  const [showAddHeadline, setShowAddHeadline] = useState(false);
  const [editingHeadline, setEditingHeadline] = useState(null);
  const [newHeadline, setNewHeadline] = useState({ tag: "BREAKING", text: "", is_active: true });

  useEffect(() => { axios.get(`${API}/settings/ticker`).then(r => setSettings(r.data)); }, []);

  const handleSave = async () => {
    setSaving(true);
    await axios.put(`${API}/settings/ticker`, settings, { headers: getAuthHeader() });
    setSaving(false);
  };

  // --- API Link Handlers ---
  const handleAddLink = () => {
    if (!newLink.name || !newLink.url) return;
    const links = settings.api_links || [];
    setSettings({ ...settings, api_links: [...links, { ...newLink, id: Date.now().toString() }] });
    setNewLink({ name: "", url: "", type: "espn", is_active: true });
    setShowAddLink(false);
  };
  const handleEditLink = (link) => { setEditingLink(link); setNewLink({ ...link }); setShowAddLink(true); };
  const handleUpdateLink = () => {
    if (!newLink.name || !newLink.url) return;
    setSettings({ ...settings, api_links: settings.api_links.map(l => l.id === editingLink.id ? { ...newLink, id: l.id } : l) });
    setNewLink({ name: "", url: "", type: "espn", is_active: true }); setEditingLink(null); setShowAddLink(false);
  };
  const handleDeleteLink = (linkId) => { if (!window.confirm("Delete this API link?")) return; setSettings({ ...settings, api_links: (settings.api_links || []).filter(l => l.id !== linkId) }); };
  const handleToggleLink = (linkId) => { setSettings({ ...settings, api_links: (settings.api_links || []).map(l => l.id === linkId ? { ...l, is_active: !l.is_active } : l) }); };

  // --- Headline Handlers ---
  const handleAddHeadline = () => {
    if (!newHeadline.text) return;
    const headlines = settings.headlines || [];
    const newItem = { ...newHeadline, id: Date.now().toString(), order: headlines.length };
    setSettings({ ...settings, headlines: [...headlines, newItem] });
    setNewHeadline({ tag: "BREAKING", text: "", is_active: true });
    setShowAddHeadline(false);
  };
  const handleEditHeadline = (h) => { setEditingHeadline(h); setNewHeadline({ tag: h.tag, text: h.text, is_active: h.is_active }); setShowAddHeadline(true); };
  const handleUpdateHeadline = () => {
    if (!newHeadline.text) return;
    setSettings({ ...settings, headlines: (settings.headlines || []).map(h => h.id === editingHeadline.id ? { ...h, ...newHeadline } : h) });
    setNewHeadline({ tag: "BREAKING", text: "", is_active: true }); setEditingHeadline(null); setShowAddHeadline(false);
  };
  const handleDeleteHeadline = (id) => { if (!window.confirm("Delete this headline?")) return; setSettings({ ...settings, headlines: (settings.headlines || []).filter(h => h.id !== id) }); };
  const handleToggleHeadline = (id) => { setSettings({ ...settings, headlines: (settings.headlines || []).map(h => h.id === id ? { ...h, is_active: !h.is_active } : h) }); };
  const handleMoveHeadline = (id, direction) => {
    const headlines = [...(settings.headlines || [])];
    const idx = headlines.findIndex(h => h.id === id);
    if (idx < 0 || (direction === -1 && idx === 0) || (direction === 1 && idx === headlines.length - 1)) return;
    [headlines[idx], headlines[idx + direction]] = [headlines[idx + direction], headlines[idx]];
    setSettings({ ...settings, headlines: headlines.map((h, i) => ({ ...h, order: i })) });
  };

  if (!settings) return <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto"></div></div>;

  const apiLinks = settings.api_links || [];
  const headlines = settings.headlines || [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white" data-testid="ticker-settings-title">Ticker Settings</h2>
        <Button onClick={handleSave} disabled={saving} data-testid="ticker-save-btn" className="bg-white text-black text-xs px-4 py-2 h-auto rounded-none">{saving ? "Saving..." : "Save Changes"}</Button>
      </div>
      
      {/* Basic Settings */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-zinc-900/50 border border-white/5">
          <label className="block text-xs text-zinc-500 uppercase mb-2">Speed</label>
          <input type="number" value={settings.speed} onChange={(e) => setSettings({ ...settings, speed: parseInt(e.target.value) || 40 })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ticker-speed-input" />
        </div>
        <div className="p-4 bg-zinc-900/50 border border-white/5">
          <label className="block text-xs text-zinc-500 uppercase mb-2">Refresh (sec)</label>
          <input type="number" value={settings.auto_refresh_seconds} onChange={(e) => setSettings({ ...settings, auto_refresh_seconds: parseInt(e.target.value) || 30 })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ticker-refresh-input" />
        </div>
        <div className="p-4 bg-zinc-900/50 border border-white/5 col-span-2">
          <label className="block text-xs text-zinc-500 uppercase mb-2">Primary Data Source</label>
          <select value={settings.data_source} onChange={(e) => setSettings({ ...settings, data_source: e.target.value })} className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="ticker-data-source-select">
            <option value="espn">ESPN Live Scores (NCAA/NFL/NBA)</option>
            <option value="custom">Custom API Links</option>
          </select>
          <p className="text-xs text-zinc-500 mt-2">ESPN provides real-time college and pro sports scores. Use &quot;Custom API Links&quot; to add your own data sources.</p>
        </div>
      </div>

      {/* Headlines Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-secondary text-lg font-bold uppercase tracking-tight text-white">Headlines</h3>
            <p className="text-xs text-zinc-500">Manage scrolling headlines that appear between game scores in the ticker</p>
          </div>
          <Button 
            onClick={() => { setShowAddHeadline(true); setEditingHeadline(null); setNewHeadline({ tag: "BREAKING", text: "", is_active: true }); }}
            data-testid="add-headline-btn"
            className="bg-zinc-800 text-white text-xs px-4 py-2 h-auto rounded-none border border-white/10 hover:bg-zinc-700"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Headline
          </Button>
        </div>

        {/* Headline settings */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-zinc-900/50 border border-white/5 flex items-center justify-between">
            <div>
              <label className="block text-xs text-zinc-500 uppercase mb-1">Show Headlines</label>
              <p className="text-xs text-zinc-600">Display headlines in the ticker</p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, show_headlines: !settings.show_headlines })}
              data-testid="show-headlines-toggle"
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.show_headlines !== false ? 'bg-orange-500' : 'bg-zinc-700'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.show_headlines !== false ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="p-4 bg-zinc-900/50 border border-white/5">
            <label className="block text-xs text-zinc-500 uppercase mb-2">Headline Frequency</label>
            <div className="flex items-center gap-2">
              <input type="number" min="1" max="20" value={settings.headline_frequency || 5} onChange={(e) => setSettings({ ...settings, headline_frequency: parseInt(e.target.value) || 5 })} className="w-20 bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" data-testid="headline-frequency-input" />
              <span className="text-xs text-zinc-500">Show a headline every N games</span>
            </div>
          </div>
        </div>

        {/* Add/Edit Headline Form */}
        {showAddHeadline && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-zinc-800/50 border border-white/10 space-y-4" data-testid="headline-form">
            <h4 className="font-secondary text-sm font-bold uppercase text-white">{editingHeadline ? "Edit Headline" : "Add New Headline"}</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-zinc-500 uppercase mb-2">Tag *</label>
                <select value={newHeadline.tag} onChange={(e) => setNewHeadline({ ...newHeadline, tag: e.target.value })} className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="headline-tag-select">
                  {HEADLINE_TAGS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs text-zinc-500 uppercase mb-2">Headline Text *</label>
                <input 
                  type="text" value={newHeadline.text} onChange={(e) => setNewHeadline({ ...newHeadline, text: e.target.value })} 
                  placeholder="e.g., Conference tournament brackets released"
                  className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" data-testid="headline-text-input"
                />
              </div>
            </div>
            {/* Preview */}
            <div className="p-3 bg-zinc-950 border border-white/5">
              <span className="text-[10px] text-zinc-600 uppercase block mb-2">Preview</span>
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${TAG_COLORS[newHeadline.tag] || 'bg-zinc-700'} text-white`}>{newHeadline.tag}</span>
                <span className="text-sm text-zinc-300">{newHeadline.text || "Enter headline text..."}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button type="button" onClick={editingHeadline ? handleUpdateHeadline : handleAddHeadline} data-testid="headline-submit-btn" className="bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none">
                {editingHeadline ? "Update Headline" : "Add Headline"}
              </Button>
              <button type="button" onClick={() => { setShowAddHeadline(false); setEditingHeadline(null); }} className="text-zinc-500 text-sm hover:text-white">Cancel</button>
            </div>
          </motion.div>
        )}

        {/* Headlines List */}
        <div className="space-y-1" data-testid="headlines-list">
          {headlines.length === 0 ? (
            <div className="text-center py-8 bg-zinc-900/50 border border-white/5">
              <MessageSquare className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No headlines configured</p>
              <p className="text-xs text-zinc-600 mt-1">Add headlines to display between game scores in the ticker</p>
            </div>
          ) : headlines.map((h, i) => (
            <HeadlineItem key={h.id} headline={h} onEdit={handleEditHeadline} onDelete={handleDeleteHeadline} onToggle={handleToggleHeadline} onMove={handleMoveHeadline} isFirst={i === 0} isLast={i === headlines.length - 1} />
          ))}
        </div>
      </div>

      {/* API Links Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-secondary text-lg font-bold uppercase tracking-tight text-white">API Links</h3>
            <p className="text-xs text-zinc-500">Add multiple score data sources for the ticker</p>
          </div>
          <Button 
            onClick={() => { setShowAddLink(true); setEditingLink(null); setNewLink({ name: "", url: "", type: "espn", is_active: true }); }}
            className="bg-zinc-800 text-white text-xs px-4 py-2 h-auto rounded-none border border-white/10 hover:bg-zinc-700"
          >
            <Plus className="w-4 h-4 mr-2" /> Add API Link
          </Button>
        </div>

        {showAddLink && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-4 bg-zinc-800/50 border border-white/10 space-y-4">
            <h4 className="font-secondary text-sm font-bold uppercase text-white">{editingLink ? "Edit API Link" : "Add New API Link"}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-zinc-500 uppercase mb-2">Name *</label>
                <input type="text" value={newLink.name} onChange={(e) => setNewLink({ ...newLink, name: e.target.value })} placeholder="e.g., MaxPreps Football" className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 uppercase mb-2">API URL *</label>
                <input type="url" value={newLink.url} onChange={(e) => setNewLink({ ...newLink, url: e.target.value })} placeholder="https://api.example.com/scores" className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 uppercase mb-2">Type</label>
                <select value={newLink.type} onChange={(e) => setNewLink({ ...newLink, type: e.target.value })} className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm">
                  <option value="espn">ESPN Format</option>
                  <option value="maxpreps">MaxPreps</option>
                  <option value="scorestream">ScoreStream</option>
                  <option value="custom">Custom JSON</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="link_active" checked={newLink.is_active} onChange={(e) => setNewLink({ ...newLink, is_active: e.target.checked })} className="w-4 h-4" />
              <label htmlFor="link_active" className="text-sm text-zinc-400">Active (include in ticker)</label>
            </div>
            <div className="flex items-center gap-4">
              <Button type="button" onClick={editingLink ? handleUpdateLink : handleAddLink} className="bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none">{editingLink ? "Update Link" : "Add Link"}</Button>
              <button type="button" onClick={() => { setShowAddLink(false); setEditingLink(null); }} className="text-zinc-500 text-sm hover:text-white">Cancel</button>
            </div>
          </motion.div>
        )}

        <div className="space-y-2">
          {apiLinks.length === 0 ? (
            <div className="text-center py-8 bg-zinc-900/50 border border-white/5">
              <Sliders className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No custom API links configured</p>
              <p className="text-xs text-zinc-600 mt-1">The ticker will use the primary data source (ESPN)</p>
            </div>
          ) : apiLinks.map((link) => (
            <div key={link.id} className={`flex items-center justify-between p-4 border ${link.is_active ? 'bg-zinc-900/50 border-white/10' : 'bg-zinc-900/20 border-white/5 opacity-60'}`}>
              <div className="flex items-center gap-4">
                <div className={`w-2 h-2 rounded-full ${link.is_active ? 'bg-green-500' : 'bg-zinc-600'}`}></div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-secondary text-sm font-bold text-white">{link.name}</span>
                    <span className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-[10px] uppercase">{link.type}</span>
                  </div>
                  <code className="text-xs text-zinc-500 block mt-1 truncate max-w-md">{link.url}</code>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleToggleLink(link.id)} className={`p-2 transition-colors ${link.is_active ? 'text-green-500 hover:text-green-400' : 'text-zinc-500 hover:text-white'}`} title={link.is_active ? "Disable" : "Enable"}>
                  {link.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => handleEditLink(link)} className="p-2 text-zinc-500 hover:text-white transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                <button onClick={() => handleDeleteLink(link.id)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default TickerTab;
